


























































































import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";

// Enums
import { DataModules } from "@/store/modules/data";
import { ProvisionFieldTypes } from "@/data/enums/provisioning";

// Types
import { IProduct } from "@/models/products";
import { IState } from "@/store";
import {
  IBlueprintField,
  IProvisionProductFieldValue,
  IProvisioningFieldsForm
} from "@/models/provisioning";

@Component({
  components: {
    "configure-field": () =>
      import(
        "@/components/app/admin/catalogue/products/provisioning/configureField.vue"
      )
  },
  computed: {
    ...mapState({
      productScope(state: IState, getters) {
        return getters[`data/${this.productDataModule}/scope`](this.productId);
      },
      product(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: this.productDataModule,
          scope: this.productScope
        });
      }
    })
  }
})
export default class ManageProvisionFieldModal extends Mixins(
  RequestPromises,
  HasValidation
) {
  // Getters
  public product!: IProduct;
  public productScope!: string;

  // Props
  @Prop({ required: true })
  public productId!: IProduct["id"];

  @Prop({ required: true })
  public field!: IBlueprintField;

  @Prop({ required: true })
  public fieldValues!: IProvisionProductFieldValue[];

  @Prop({ default: "" })
  public fieldError!: string;

  @Prop({ default: ProvisionFieldTypes.PRODUCT_FIELD })
  public type!: ProvisionFieldTypes;

  // Variables

  public guardFunctionalities = ["staff_get_product"];

  public form: IProvisioningFieldsForm = {
    products: []
  };
  public formClone: IProvisioningFieldsForm = {
    products: []
  };

  public productDataModule: DataModules = DataModules.CATALOGUE;
  public provisionDataModule: DataModules = DataModules.PROVISIONING;

  public isLoading = false;
  public isReloading = false;
  public requestError = false;

  public isProcessing = false;

  public created() {
    if (!this.isContentAvailable) {
      return;
    }
    this.isLoading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      await this.getProductData();
      this.initiateForm();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProductData(ignoreStored = false) {
    return this.$store.dispatch(`data/${this.productDataModule}/get`, {
      id: this.productId,
      scope: this.productScope,
      vm: this,
      ignoreStored
    });
  }

  public get i18n() {
    return {
      title: this.$t("_.configure_type_field_obj", {
        type: (this.isProductField
          ? this.$t("_.blueprint")
          : this.$t("_.attribute")
        ).toString(),
        obj: this.field.field_label
      }),
      description: (this.isProductField
        ? this.$t("_sentence.configure_blueprint_field")
        : this.$t("_sentence.configure_attribute_field")
      ).toString()
    };
  }

  public get isProductField() {
    return this.type === ProvisionFieldTypes.PRODUCT_FIELD;
  }

  public getProductObj(productId: IProduct["id"]) {
    if (this.product.id !== productId) {
      return this.$_.some(this.product.products_options, ["id", productId])
        ? this.$_.find(this.product.products_options, ["id", productId])
        : this.$_.find(this.product.products_attributes, ["id", productId]);
    }

    return this.product;
  }

  public initiateForm() {
    this.$set(this.form, "products", this.formProducts);
    this.formClone = this.$_.cloneDeep(this.form);
  }

  public get formProducts() {
    return this.$_.map(this.fieldValues, productFieldValue => {
      return this.$_.merge(
        {
          id: productFieldValue.id
        },
        this.isProductField
          ? {
              provision_field_values: this.getFieldValues(
                productFieldValue.provision_field_values
              )
            }
          : {
              provision_configuration_attribute_values: this.getFieldValues(
                productFieldValue.provision_configuration_attribute_values
              )
            }
      );
    });
  }

  public getFieldValues(fieldValues) {
    const fieldValue = this.$_.find(fieldValues, [
      this.isProductField ? "blueprint_field_id" : "attribute_id",
      this.field.id
    ]);
    const result = {};

    result[`${this.field.name}`] = fieldValue ? fieldValue.value : null;

    return result;
  }

  public getIndex(productId) {
    return this.$_.findIndex(
      this.isProductField
        ? this.newFormValues("provision_field_values")
        : this.newFormValues("provision_configuration_attribute_values"),
      ["id", productId]
    );
  }

  public async validateBeforeSubmit() {
    if (!this.canSave) {
      return;
    }

    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      this.isProcessing = true;
      this.isProductField
        ? await this.saveProductFieldsValues()
        : await this.saveProductAttributesValues();

      this.$buefy.toast.open({
        message: this.$t(`_.object_successfully_updated`, {
          object: this.$t("_.provisioning_fields")
        }) as string
      });

      this.$emit("success", true);
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }

    this.isProcessing = false;
  }

  public saveProductFieldsValues() {
    return this.$store.dispatch(
      `data/${this.provisionDataModule}/updateProductFieldsValues`,
      {
        id: this.productId,
        data: {
          products: this.newFormValues("provision_field_values")
        }
      }
    );
  }

  public saveProductAttributesValues() {
    return this.$store.dispatch(
      `data/${this.provisionDataModule}/updateProductAttributesValues`,
      {
        id: this.productId,
        data: {
          products: this.newFormValues(
            "provision_configuration_attribute_values"
          )
        }
      }
    );
  }

  public get buttonIsDisabled() {
    return this.$_.isEqual(this.form, this.formClone);
  }

  public newFormValues(fieldKey: string) {
    const newValues: any[] = [];

    this.$_.forEach(this.form.products, (product, index) => {
      if (
        !this.$_.isEqual(
          product[fieldKey],
          this.formClone.products[index][fieldKey]
        )
      ) {
        newValues.push(product);
      }
    });

    return newValues;
  }

  public get canSave() {
    return this.isProductField
      ? this.$userCan("update_product_provision_field_values")
      : this.$userCan(
          "update_product_provision_configuration_attribute_values"
        );
  }

  public get isContentAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: this.productDataModule,
      scope: this.productScope,
      vm: this
    });
  }
}
